/**
 * Компонент диалога импорта данных KLS
 * 
 * Функциональность:
 * - Выбор файла для импорта
 * - Выбор формата файла (CSV, XLSX, JSON)
 * - Выбор типа сущности (qual_type, qual, kls)
 * - Настройка маппинга полей
 * - Выбор стратегии обработки дубликатов
 * - Предпросмотр данных перед импортом
 * - Отображение прогресса и результатов импорта
 */

import { useState, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { klsImportExportApi } from "@/services/kls-api.service";
import type {
  ImportFormat,
  EntityType,
  DuplicateStrategy,
  ImportPreviewResponse,
} from "@/types/api-v2";
import { Upload, FileText, AlertCircle, Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface ImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entityType?: EntityType; // Предустановленный тип сущности
  onSuccess?: () => void;
}

export default function ImportDialog({
  isOpen,
  onClose,
  entityType: defaultEntityType,
  onSuccess,
}: ImportDialogProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [file, setFile] = useState<File | null>(null);
  const [format, setFormat] = useState<ImportFormat>("csv");
  const [entityType, setEntityType] = useState<EntityType>(defaultEntityType || "qual_type");
  const [duplicateStrategy, setDuplicateStrategy] = useState<DuplicateStrategy>("error");
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [previewData, setPreviewData] = useState<ImportPreviewResponse | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  
  // Мутация предпросмотра
  const previewMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Файл не выбран");
      return klsImportExportApi.previewImport(file, format, entityType, mapping);
    },
    onSuccess: (data) => {
      setPreviewData(data);
      toast({
        title: "Предпросмотр готов",
        description: `Найдено ${data.total_rows} строк, из них валидных: ${data.valid_rows}`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка предпросмотра",
        description: error?.message || "Не удалось загрузить предпросмотр",
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsPreviewing(false);
    },
  });
  
  // Мутация импорта
  const importMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Файл не выбран");
      return klsImportExportApi.import(file, format, entityType, duplicateStrategy, mapping);
    },
    onSuccess: (data) => {
      toast({
        title: "Импорт завершен",
        description: `Импортировано: ${data.imported}, обновлено: ${data.updated}, пропущено: ${data.skipped}, ошибок: ${data.errors}`,
      });
      onSuccess?.();
      handleClose();
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка импорта",
        description: error?.message || "Не удалось выполнить импорт",
        variant: "destructive",
      });
    },
  });
  
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Автоматическое определение формата по расширению
      const ext = selectedFile.name.split('.').pop()?.toLowerCase();
      if (ext === 'csv') setFormat('csv');
      else if (ext === 'xlsx' || ext === 'xls') setFormat('xlsx');
      else if (ext === 'json') setFormat('json');
      setPreviewData(null);
    }
  };
  
  const handlePreview = () => {
    if (!file) {
      toast({
        title: "Ошибка",
        description: "Выберите файл для импорта",
        variant: "destructive",
      });
      return;
    }
    setIsPreviewing(true);
    previewMutation.mutate();
  };
  
  const handleImport = () => {
    if (!file) {
      toast({
        title: "Ошибка",
        description: "Выберите файл для импорта",
        variant: "destructive",
      });
      return;
    }
    if (!previewData) {
      toast({
        title: "Ошибка",
        description: "Сначала выполните предпросмотр",
        variant: "destructive",
      });
      return;
    }
    importMutation.mutate();
  };
  
  const handleClose = () => {
    setFile(null);
    setFormat("csv");
    setEntityType(defaultEntityType || "qual_type");
    setDuplicateStrategy("error");
    setMapping({});
    setPreviewData(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Импорт данных</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Выбор файла */}
          <div className="space-y-2">
            <Label>Файл для импорта</Label>
            <div className="flex gap-2">
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls,.json"
                onChange={handleFileSelect}
                className="flex-1"
              />
              {file && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  <span>{file.name}</span>
                  <span className="text-xs">({(file.size / 1024).toFixed(1)} KB)</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Настройки импорта */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Формат файла</Label>
              <Select value={format} onValueChange={(v) => setFormat(v as ImportFormat)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Тип сущности</Label>
              <Select value={entityType} onValueChange={(v) => setEntityType(v as EntityType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="qual_type">Типы квалификаторов</SelectItem>
                  <SelectItem value="qual">Квалификаторы</SelectItem>
                  <SelectItem value="kls">Разделы классификаторов</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Стратегия обработки дубликатов */}
          <div className="space-y-2">
            <Label>Стратегия обработки дубликатов</Label>
            <Select
              value={duplicateStrategy}
              onValueChange={(v) => setDuplicateStrategy(v as DuplicateStrategy)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="error">Ошибка (прервать импорт)</SelectItem>
                <SelectItem value="skip">Пропустить дубликаты</SelectItem>
                <SelectItem value="update">Обновить существующие</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Кнопка предпросмотра */}
          <div className="flex justify-end">
            <Button
              onClick={handlePreview}
              disabled={!file || isPreviewing || importMutation.isPending}
            >
              {isPreviewing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Загрузка предпросмотра...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Предпросмотр
                </>
              )}
            </Button>
          </div>
          
          {/* Результаты предпросмотра */}
          {previewData && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Результаты предпросмотра</h4>
                <div className="flex gap-2">
                  <Badge variant={previewData.valid_rows > 0 ? "default" : "destructive"}>
                    Валидных: {previewData.valid_rows}
                  </Badge>
                  <Badge variant={previewData.invalid_rows > 0 ? "destructive" : "default"}>
                    Ошибок: {previewData.invalid_rows}
                  </Badge>
                </div>
              </div>
              
              {/* Предпросмотр данных */}
              {previewData.preview.length > 0 && (
                <div className="border rounded-md overflow-hidden">
                  <div className="max-h-60 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {Object.keys(previewData.preview[0]).map((key) => (
                            <TableHead key={key} className="text-xs">
                              {key}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewData.preview.map((row, idx) => (
                          <TableRow key={idx}>
                            {Object.values(row).map((value, cellIdx) => (
                              <TableCell key={cellIdx} className="text-xs">
                                {String(value || "-")}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              
              {/* Ошибки валидации */}
              {previewData.errors.length > 0 && (
                <div className="space-y-2">
                  <h5 className="text-sm font-medium text-destructive flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Ошибки валидации (первые {previewData.errors.length})
                  </h5>
                  <div className="border rounded-md p-4 max-h-40 overflow-y-auto space-y-2">
                    {previewData.errors.map((error, idx) => (
                      <div key={idx} className="text-sm">
                        <span className="font-medium">Строка {error.row}:</span>{" "}
                        {error.errors.join(", ")}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Прогресс импорта */}
          {importMutation.isPending && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Выполняется импорт...</span>
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
              <Progress value={undefined} className="w-full" />
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={importMutation.isPending}>
            Отмена
          </Button>
          <Button
            onClick={handleImport}
            disabled={!previewData || previewData.valid_rows === 0 || importMutation.isPending}
          >
            {importMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Импорт...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Импортировать
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

